// App-level tour store for the tour-builder. At ~100 items we load ALL artifacts
// in one GET and DERIVE tours client-side by grouping on `tourTags` (tours.ts
// groupByTour). In parallel we load the durable TOUR#<slug> order records.
//
// Persistence model:
//   - MEMBERSHIP (add/remove) is durable: PUT /artifacts/{id} { tourTags } writes
//     the FULL new tourTags array; we then refresh() from the server.
//   - ORDER (rearrange) is durable in DynamoDB: each tour is a TOUR#<slug> record
//     whose `order` array holds the arrangement. reorder() updates the in-memory
//     cache instantly (optimistic) and writes PUT /tours/{slug} { order } in the
//     background, minting the record on first use. Order is regenerable from the
//     scan if a record is missing (orderItems appends unordered members).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  createTour,
  exportTour as exportTourApi,
  listArtifacts,
  listTours,
  putTour,
  updateArtifact,
} from "./api";
import type { Artifact, Quote, QuoteSlide, UpdateTourRequest } from "./types";
import { groupByTour, type TourGroup } from "./tours";
import {
  getOrder,
  getQuotes,
  getQuoteSlides,
  hydrateOrders,
  setOrder,
  setQuotesCache,
  setQuoteSlidesCache,
} from "./tourStore";

/** Lifecycle of a "Download for iPad" (offline HTML) export, per tour slug. */
export type ExportStatus = "idle" | "generating" | "done" | "error";

/** Per-slug export state surfaced to the views (defaults to `idleExport`). */
export interface ExportState {
  status: ExportStatus;
  /** Human-readable note for the current status (errors, success hint). */
  message: string | null;
  /** The same-origin attachment URL from the last successful export. */
  url: string | null;
}

/** The state for a slug that has never been exported this session. */
const idleExport: ExportState = { status: "idle", message: null, url: null };

/**
 * Sentinel passed to `exportTour` to build the MULTI-TOUR catalog — ONE offline
 * file whose front page is a grid of tour thumbnails, containing every tour. The
 * server special-cases `POST /tours/__all__/export` (reusing the per-tour route);
 * it can't collide with a real tour slug (slugs never contain underscores). The
 * server returns `slug: "all-tours"`, so the download saves as `all-tours.html`.
 */
export const ALL_TOURS_SLUG = "__all__";

export interface TourStore {
  /** Derived tours (one per distinct tourTag slug), sorted by slug. */
  groups: TourGroup[];
  /** Every artifact (the raw scan), for candidate pickers / counts. */
  allArtifacts: Artifact[];
  loading: boolean;
  error: string | null;
  /** Re-fetch every artifact + tour record and re-derive tours. */
  refresh: () => Promise<void>;
  /** Add an artifact to a tour = add `slug` to its tourTags (idempotent). */
  addToTour: (artifactId: string, slug: string) => Promise<void>;
  /** Remove an artifact from a tour = remove `slug` from its tourTags. */
  removeFromTour: (artifactId: string, slug: string) => Promise<void>;
  /**
   * Persist a new display order for a tour: updates the cache + re-sorts locally
   * (optimistic, instant), then writes it durably to DynamoDB in the background.
   */
  reorder: (slug: string, orderedIds: string[]) => void;
  /** The tour's saved quotes, read from the cache (instant; survives a tours-API hiccup). */
  quotesFor: (slug: string) => Quote[];
  /**
   * Persist a tour's quotes: optimistic cache update (instant) + durable
   * PUT /tours/{slug} { quotes }, minting the TOUR# record on first use. Resolves
   * when the write settles; on failure the cache is kept and `error` is set.
   */
  setQuotes: (slug: string, quotes: Quote[]) => Promise<void>;
  /** The tour's saved quote slides, read from the cache (instant). */
  quoteSlidesFor: (slug: string) => QuoteSlide[];
  /**
   * Add a quote slide to a tour and PREPEND it (slide id at the front of `order`).
   * Optimistic cache update (instant) + durable PUT /tours/{slug} { quoteSlides, order },
   * minting the TOUR# record on first use. Resolves when the write settles.
   */
  addQuoteSlide: (slug: string, slide: QuoteSlide) => Promise<void>;
  /** Remove a quote slide from a tour (drops it from both `quoteSlides` and `order`). */
  removeQuoteSlide: (slug: string, id: string) => Promise<void>;
  /**
   * (Re)generate a tour's self-contained OFFLINE HTML on the server, then trigger
   * a same-origin download of it. Drives per-slug `exportState`. Resolves to the
   * export response on success, or null on failure (the error is in exportState).
   */
  exportTour: (slug: string) => Promise<void>;
  /** Per-slug export status/message/url (defaults to idle for unseen slugs). */
  exportState: (slug: string) => ExportState;
}

export function useTours(): TourStore {
  const [allArtifacts, setAllArtifacts] = useState<Artifact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Bumped by reorder() so the `groups` memo re-reads the order cache without a
  // server refresh.
  const [orderRev, setOrderRev] = useState(0);
  // Per-slug "Download for iPad" export lifecycle (offline HTML generate +
  // download). Sparse: a slug absent from the map is treated as `idleExport`.
  const [exports, setExports] = useState<Record<string, ExportState>>({});
  // Serializes per-slug order writes so rapid drags don't race; also lets us skip
  // the PUT-404 probe once we know a tour's TOUR# record exists.
  const knownSlugs = useRef<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [items, tours] = await Promise.all([
        listArtifacts("all"),
        // Order is a nicety — never fail the whole load if the tours API hiccups.
        listTours().catch(() => []),
      ]);
      hydrateOrders(tours);
      knownSlugs.current = new Set(tours.map((t) => t.slug).filter(Boolean));
      setAllArtifacts(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load artifacts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Derive tours from the scan + the saved per-tour order. Re-runs when the
  // artifact set changes (refresh) or the order changes (reorder bumps orderRev).
  const groups = useMemo(
    () => groupByTour(allArtifacts),
    // orderRev is an intentional dependency: groupByTour reads the order cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allArtifacts, orderRev],
  );

  const addToTour = useCallback(
    async (artifactId: string, slug: string) => {
      const a = allArtifacts.find((x) => x.id === artifactId);
      const current = a?.tourTags ?? [];
      if (current.includes(slug)) return; // already a member — no-op
      const next = [...current, slug];
      await updateArtifact(artifactId, { tourTags: next });
      await refresh();
    },
    [allArtifacts, refresh],
  );

  const removeFromTour = useCallback(
    async (artifactId: string, slug: string) => {
      const a = allArtifacts.find((x) => x.id === artifactId);
      const current = a?.tourTags ?? [];
      if (!current.includes(slug)) return; // not a member — no-op
      const next = current.filter((t) => t !== slug);
      await updateArtifact(artifactId, { tourTags: next });
      await refresh();
    },
    [allArtifacts, refresh],
  );

  // Durable PUT to a tour record, minting the TOUR#<slug> record on first use
  // (PUT 404 -> POST {name:slug} -> PUT). Canonical tag slugs round-trip through
  // slugify, so the minted record's slug equals the tag. Shared by order + quotes
  // writes (the only difference is the patch body).
  const mintOrPut = useCallback(
    async (slug: string, patch: UpdateTourRequest) => {
      if (knownSlugs.current.has(slug)) {
        await putTour(slug, patch);
        return;
      }
      try {
        await putTour(slug, patch);
        knownSlugs.current.add(slug);
      } catch (err) {
        if (!(err instanceof ApiError) || err.status !== 404) throw err;
        const created = await createTour(slug).catch(() => null);
        const target = created?.slug ?? slug;
        await putTour(target, patch);
        knownSlugs.current.add(slug);
      }
    },
    [],
  );

  const persistOrder = useCallback(
    (slug: string, ids: string[]) => mintOrPut(slug, { order: ids }),
    [mintOrPut],
  );

  const reorder = useCallback(
    (slug: string, orderedIds: string[]) => {
      // Optimistic: update the cache + re-sort immediately (no network wait).
      setOrder(slug, orderedIds);
      setOrderRev((n) => n + 1);
      // Durable: persist to DynamoDB in the background; surface failures (the
      // order is still kept locally via the write-through cache).
      void persistOrder(slug, orderedIds)
        .then(() => setError(null))
        .catch((err) =>
          setError(
            `Couldn't save the new order to the server (kept on this device): ${
              err instanceof Error ? err.message : "unknown error"
            }`,
          ),
        );
    },
    [persistOrder],
  );

  // Read a tour's quotes straight from the cache so the panel opens instantly and
  // keeps working through a tours-API hiccup (hydrated on load / set on save).
  const quotesFor = useCallback((slug: string): Quote[] => getQuotes(slug), []);

  // Durable quote write. Optimistic cache update (instant) + PUT /tours/{slug}
  // { quotes } in the same mint-on-first-use way as reorder. Mirrors reorder's
  // failure handling: keep the local cache, surface a friendly `error`.
  const setQuotes = useCallback(
    async (slug: string, quotes: Quote[]) => {
      // Optimistic: update the cache immediately (no network wait).
      setQuotesCache(slug, quotes);
      setOrderRev((n) => n + 1);
      // Durable: persist to DynamoDB; surface failures (quotes are still kept
      // locally via the write-through cache).
      try {
        await mintOrPut(slug, { quotes });
        setError(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : "unknown error";
        setError(
          `Couldn't save the quotes to the server (kept on this device): ${message}`,
        );
        throw err; // let the panel show its own inline "couldn't save" state too
      }
    },
    [mintOrPut],
  );

  // Read a tour's quote slides straight from the cache (instant, resilient).
  const quoteSlidesFor = useCallback(
    (slug: string): QuoteSlide[] => getQuoteSlides(slug),
    [],
  );

  // Durable quote-slide writes. Both update the optimistic caches (quote-slide
  // content + the order ids) instantly, then persist { quoteSlides, order } in one
  // PUT via the same mint-on-first-use path as reorder/setQuotes.
  const persistQuoteSlides = useCallback(
    async (slug: string, slides: QuoteSlide[], order: string[]) => {
      setQuoteSlidesCache(slug, slides);
      setOrder(slug, order);
      setOrderRev((n) => n + 1);
      try {
        await mintOrPut(slug, { quoteSlides: slides, order });
        setError(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : "unknown error";
        setError(
          `Couldn't save the quote slide to the server (kept on this device): ${message}`,
        );
        throw err;
      }
    },
    [mintOrPut],
  );

  const addQuoteSlide = useCallback(
    async (slug: string, slide: QuoteSlide) => {
      const slides = [...getQuoteSlides(slug), slide];
      // Prepend: the new slide leads the tour. De-dupe defensively.
      const order = [slide.id, ...getOrder(slug).filter((id) => id !== slide.id)];
      await persistQuoteSlides(slug, slides, order);
    },
    [persistQuoteSlides],
  );

  const removeQuoteSlide = useCallback(
    async (slug: string, id: string) => {
      const slides = getQuoteSlides(slug).filter((s) => s.id !== id);
      const order = getOrder(slug).filter((x) => x !== id);
      await persistQuoteSlides(slug, slides, order);
    },
    [persistQuoteSlides],
  );

  // "Download for iPad": ask the server to (re)build the offline HTML, then save
  // it via a same-origin attachment anchor. The anchor click runs SYNCHRONOUSLY
  // right after the await so modern iPadOS Safari treats it as user-initiated.
  const exportTour = useCallback(async (slug: string) => {
    setExports((m) => ({
      ...m,
      [slug]: { status: "generating", message: null, url: null },
    }));
    try {
      const res = await exportTourApi(slug);
      triggerDownload(res.url, `${res.slug || slug}.html`);
      setExports((m) => ({
        ...m,
        [slug]: {
          status: "done",
          message: "Saved to Files — open it there or AirDrop it.",
          url: res.url,
        },
      }));
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Export failed — try again.";
      setExports((m) => ({
        ...m,
        [slug]: { status: "error", message, url: null },
      }));
    }
  }, []);

  const exportState = useCallback(
    (slug: string): ExportState => exports[slug] ?? idleExport,
    [exports],
  );

  return {
    groups,
    allArtifacts,
    loading,
    error,
    refresh,
    addToTour,
    removeFromTour,
    reorder,
    quotesFor,
    setQuotes,
    quoteSlidesFor,
    addQuoteSlide,
    removeQuoteSlide,
    exportTour,
    exportState,
  };
}

/**
 * Save a same-origin URL to the device by clicking a transient `<a download>`.
 * The tours API serves the export with `Content-Disposition: attachment`, so on
 * iPadOS Safari this lands in the Files app for fully-offline viewing.
 */
function triggerDownload(url: string, filename: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}
